import React, { useState, useEffect } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { Card, CardTitle, Insight, MetricCard, Grid } from '../components/UI';
import { supabase } from '../supabaseClient';

// 28 Sep 2026 — /api/sana-proxy now requires a signed-in admin.
async function adminJwt() {
  try { const { data } = await supabase.auth.getSession(); return (data && data.session && data.session.access_token) || ''; } catch (_e) { return ''; }
}

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const SANA_URL = '/api/sana-proxy';

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => { const h = () => setMob(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return mob;
}

function SanaEditorBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isMobile = useIsMobile();
  const [metrics,  setMetrics]  = useState(null);
  const [testQ,    setTestQ]    = useState('');
  const [testRes,  setTestRes]  = useState(null);
  const [testing,  setTesting]  = useState(false);
  const [tab,      setTab]      = useState('metrics');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase
          .from('sana_invocations')
          .select('fallback_level,is_regex,latency_ms,result_count,intent_type,intent_city,intent_country')
          .order('created_at', { ascending: false })
          .limit(500);
        if (!data) return;
        const total = data.length;
        const lvls  = [0,1,2,3].map(l => ({ l, n: data.filter(r => r.fallback_level === l).length }));
        const regex = data.filter(r => r.is_regex).length;
        const avgMs = Math.round(data.reduce((s,r) => s + (r.latency_ms||0), 0) / total);
        const types = {};
        data.forEach(r => { if (r.intent_type) types[r.intent_type] = (types[r.intent_type]||0)+1; });
        const topTypes = Object.entries(types).sort((a,b)=>b[1]-a[1]).slice(0,8);
        setMetrics({ total, lvls, regex, avgMs, topTypes });
      } catch (_e) {}
    })();
  }, []);

  async function runTest() {
    if (!testQ.trim()) return;
    setTesting(true); setTestRes(null);
    try {
      const res = await fetch(SANA_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (await adminJwt()) },
        body: JSON.stringify({ messages: [{ role: 'user', content: testQ }], source: 'qa_test' }),
      });
      const data = await res.json();
      setTestRes(data);
    } catch (_e) { setTestRes({ error: 'Request failed' }); }
    setTesting(false);
  }

  const pct = (n) => metrics ? Math.round(n/metrics.total*100) : 0;

  return (
    <div style={{ fontFamily: F, padding: isMobile ? '16px 14px 80px' : '24px 28px' }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 500, margin: 0, color: 'var(--color-text-primary)' }}>Sana Editor</h2>
        <p style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
          Monitor Sana query quality and test queries against the live API.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid var(--color-border-tertiary)' }}>
        {['metrics', 'test'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 18px', border: 'none', borderBottom: tab === t ? '2px solid #314F5C' : '2px solid transparent',
            background: 'none', color: tab === t ? '#314F5C' : 'var(--color-text-secondary)',
            fontSize: '.78rem', fontWeight: tab === t ? 500 : 400, cursor: 'pointer', fontFamily: F,
            marginBottom: -1,
          }}>{t === 'metrics' ? 'Metrics' : 'Live test'}</button>
        ))}
      </div>

      {tab === 'metrics' && metrics && (
        <>
          <Grid cols={4} style={{ marginBottom: 20 }}>
            <MetricCard label="Total queries (last 500)" value={metrics.total.toLocaleString()} accent="#314F5C" />
            <MetricCard label="Exact match (L0)" value={pct(metrics.lvls[0].n)+'%'} sub={metrics.lvls[0].n+' queries'} trend="up" accent="#28a745" />
            <MetricCard label="No results (L3)" value={pct(metrics.lvls[3].n)+'%'} sub={metrics.lvls[3].n+' queries'} trend="down" accent="#dc3545" />
            <MetricCard label="Regex fast-path" value={pct(metrics.regex)+'%'} sub="no Claude call" accent="#819D84" />
          </Grid>

          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Fallback breakdown</CardTitle>
            {metrics.lvls.map(({ l, n }) => {
              const labels = ['L0 — exact match','L1 — broad type','L2 — city dropped','L3 — no results'];
              const colors = ['#28a745','#fd7e14','#dc6c2c','#dc3545'];
              const p = pct(n);
              return (
                <div key={l} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <div style={{ width:140, fontSize:'.72rem', color:'var(--color-text-secondary)' }}>{labels[l]}</div>
                  <div style={{ flex:1, height:8, borderRadius:4, background:'var(--color-background-tertiary)', overflow:'hidden' }}>
                    <div style={{ height:'100%', width:p+'%', background:colors[l], borderRadius:4 }} />
                  </div>
                  <div style={{ width:60, textAlign:'right', fontSize:'.72rem', color:colors[l], fontWeight:500 }}>{n} ({p}%)</div>
                </div>
              );
            })}
          </Card>

          <Card>
            <CardTitle>Top query types</CardTitle>
            {metrics.topTypes.map(([type, n]) => (
              <div key={type} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', borderBottom:'.5px solid var(--color-border-tertiary)', fontSize:'.75rem' }}>
                <span style={{ color:'var(--color-text-primary)' }}>{type}</span>
                <span style={{ color:t.mut, fontWeight:500 }}>{n}</span>
              </div>
            ))}
          </Card>
        </>
      )}

      {tab === 'test' && (
        <Card>
          <CardTitle>Live query test</CardTitle>
          <p style={{ fontSize:'.75rem', color:'var(--color-text-secondary)', margin:'0 0 14px' }}>
            Fire any query against the live Sana API and inspect the full response.
          </p>
          <div style={{ display:'flex', gap:8, marginBottom:16 }}>
            <input value={testQ} onChange={e=>setTestQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runTest()}
              placeholder="e.g. spa near me, dentist in Bangkok..."
              style={{ flex:1, padding:'8px 12px', borderRadius:8, border:'1px solid var(--color-border-secondary)', background:'var(--color-background-secondary)', color:'var(--color-text-primary)', fontSize:'.78rem', fontFamily:F }} />
            <button onClick={runTest} disabled={!testQ.trim()||testing}
              style={{ padding:'8px 18px', borderRadius:8, background:t.accent, color:'#AFE1F6', border:'none', fontSize:'.78rem', fontWeight:500, cursor:'pointer', fontFamily:F }}>
              {testing ? 'Running…' : '▶ Run'}
            </button>
          </div>
          {testRes && (
            <div>
              {testRes.error && <Insight type="warn">{testRes.error}</Insight>}
              {testRes.message && (
                <div style={{ background:'var(--color-background-tertiary)', borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
                  <div style={{ fontSize:'.65rem', color:'var(--color-text-secondary)', marginBottom:4, textTransform:'uppercase', letterSpacing:'.04em' }}>Sana reply</div>
                  <div style={{ fontSize:'.78rem', color:'var(--color-text-primary)' }}>{testRes.message}</div>
                </div>
              )}
              {testRes.intent && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
                  {Object.entries(testRes.intent).filter(([,v])=>v!=null).map(([k,v])=>(
                    <div key={k} style={{ fontSize:'.65rem', padding:'3px 8px', borderRadius:10, background:'var(--color-background-secondary)', border:'1px solid var(--color-border-secondary)', color:'var(--color-text-secondary)' }}>
                      <span style={{ fontWeight:500 }}>{k}:</span> {String(v)}
                    </div>
                  ))}
                </div>
              )}
              {testRes.locations && testRes.locations.length > 0 && (
                <div>
                  <div style={{ fontSize:'.65rem', color:'var(--color-text-secondary)', marginBottom:6, textTransform:'uppercase', letterSpacing:'.04em' }}>{testRes.locations.length} results</div>
                  {testRes.locations.slice(0,5).map(l => (
                    <div key={l.id} style={{ padding:'6px 0', borderBottom:'.5px solid var(--color-border-tertiary)', fontSize:'.75rem' }}>
                      <span style={{ fontWeight:500, color:'var(--color-text-primary)' }}>{l.name}</span>
                      <span style={{ color:'var(--color-text-secondary)', marginLeft:8 }}>{[l.type,l.city,l.country].filter(Boolean).join(' · ')}</span>
                    </div>
                  ))}
                </div>
              )}
              {testRes.locations && testRes.locations.length === 0 && (
                <Insight type="warn">No results returned (L3 fallback)</Insight>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

export default function SanaEditor(props) {
  if (props && props.embed) return <SanaEditorBody {...props} />;
  return <ConsoleShell title="Sana Editor"><SanaEditorBody {...props} /></ConsoleShell>;
}
